/**
 * @fileoverview Own browser workspace availability for the current authenticated identity.
 *
 * Non-admin employees load the firm-wide section allowlist from `/api/app-access`
 * (`kc-internal-backend/functions/employee_app_access.py`), refreshed on focus and
 * every minute. Admins see every section; users still onboarding get none, and
 * `homePath` falls back to personal settings. Selection is keyed to the employee
 * so an identity change cannot expose the previous account's sections.
 *
 * `AuthContext.js` owns session validation; `appAccess.js` owns route/section
 * mapping; `App.js` and the sidebar consume availability. The backend enforces
 * the same allowlist and owns resource authorization; visibility is not permission.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AuthContext } from './AuthContext'
import api from './api'
import { sectionForLocation, WORKSPACE_PATHS } from './appAccess'

export const AppAccessContext = createContext({
    canAccess: () => false,
    canAccessLocation: () => false,
    homePath: '/settings?tab=personal',
})

 
export function useAppAccess() {
    return useContext(AppAccessContext)
}

 
export function AppAccessProvider({ children }) {
    const { isAuthenticated, isAdmin, employeeId, requiresOnboarding } = useContext(AuthContext)
    const needsSelection = isAuthenticated && !isAdmin && !requiresOnboarding
    const [selection, setSelection] = useState(null)
    const [error, setError] = useState(false)
    const [retry, setRetry] = useState(0)

    useEffect( 
    () => {
        let active = true
        setSelection(null)
        setError(false)
        if (!needsSelection) return () => { active = false }
        const refresh =  
        async () => {
            try {
                const { data } = await api.get('/app-access')
                if (!Array.isArray(data.enabled_sections)) throw new Error('Invalid employee access response')
                if (active) {
                    setSelection((previous) => previous?.employeeId === employeeId && previous.revision > data.revision
                        ? previous : { employeeId, enabled: data.enabled_sections, revision: data.revision })
                    setError(false)
                }
            } catch (_error) {
                if (active) setError(true)
            }
        }
        refresh()
        window.addEventListener('focus', refresh)
        const interval = window.setInterval(refresh, 60000)
        return () => {
            active = false
            window.removeEventListener('focus', refresh)
            window.clearInterval(interval)
        }
    }, [needsSelection, employeeId, retry])

    const currentSelection = selection && selection.employeeId === employeeId ? selection.enabled : null
    const canAccess = useCallback( 
    (section) => !section || isAdmin === true || (section !== 'admin' && currentSelection?.includes(section) === true), [isAdmin, currentSelection])
    const value = useMemo(() => ({
        canAccess,
        canAccessLocation: (location) => canAccess(sectionForLocation(location)),
        homePath: WORKSPACE_PATHS.find(([section]) => canAccess(section))?.[1] || '/settings?tab=personal',
    }), [canAccess])

    if (needsSelection && error) {
        return <div role="alert" className="p-4">Employee access could not be loaded. <button type="button" onClick={() => setRetry((value) => value + 1)}>Retry</button></div>
    }
    if (needsSelection && currentSelection === null) return <div role="status" className="p-4">Loading workspace…</div>

    return <AppAccessContext.Provider value={value}>{children}</AppAccessContext.Provider>
}
